import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Tag, X } from 'lucide-react';

/**
 * Composant d'autocomplétion pour sélectionner une marque
 * Extrait automatiquement les marques existantes depuis les produits
 * Permet de créer une nouvelle marque si elle n'existe pas
 */
const BrandAutocomplete = ({ 
  value = '',
  onChange,
  placeholder = "Tapez pour rechercher ou créer une marque...",
  allProducts = []
}) => {
  const [query, setQuery] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Mettre à jour la query quand la valeur externe change
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Extraire les marques uniques depuis les produits existants
  const existingBrands = [...new Set(
    allProducts
      .map(p => p?.brand)
      .filter(b => b && String(b).trim())
      .map(b => String(b).trim())
  )].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

  // Filtrer les marques selon la recherche
  const filteredBrands = existingBrands.filter(brand => {
    if (!query.trim()) return true;
    return brand.toLowerCase().includes(query.toLowerCase());
  });

  // Vérifier si la query correspond exactement à une marque existante
  const isExactMatch = existingBrands.some(b => b.toLowerCase() === query.toLowerCase());

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gestion du clavier
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    const totalOptions = filteredBrands.length + (query.trim() && !isExactMatch ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex < filteredBrands.length) {
          handleSelect(filteredBrands[highlightedIndex]);
        } else if (query.trim() && !isExactMatch) {
          handleCreateNew();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Sélection d'une marque existante
  const handleSelect = (brand) => {
    setQuery(brand);
    onChange(brand);
    setIsOpen(false);
  };

  // Créer une nouvelle marque
  const handleCreateNew = () => {
    const newBrand = query.trim();
    if (newBrand) {
      setQuery(newBrand);
      onChange(newBrand);
      setIsOpen(false);
    }
  };

  // Effacer la sélection
  const handleClear = () => {
    setQuery('');
    onChange('');
    inputRef.current?.focus();
  };

  // Compter le nombre de produits par marque
  const getBrandCount = (brand) => {
    return allProducts.filter(p => p?.brand === brand).length;
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input de recherche */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            const newValue = e.target.value;
            setQuery(newValue);
            onChange(newValue);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full bg-slate-50 dark:bg-slate-800 p-3 pr-20 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        {/* Icônes à droite */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
          <ChevronDown 
            className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown des suggestions */}
      {isOpen && (filteredBrands.length > 0 || (query.trim() && !isExactMatch)) && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl max-h-[300px] overflow-y-auto">
          {/* Marques existantes */}
          {filteredBrands.map((brand, index) => {
            const productCount = getBrandCount(brand);
            
            return (
              <button
                key={brand}
                type="button"
                onClick={() => handleSelect(brand)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full text-left p-3 border-b dark:border-slate-700 last:border-0 transition-all flex items-center justify-between ${
                  index === highlightedIndex 
                    ? 'bg-blue-50 dark:bg-blue-900/20' 
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-sm dark:text-white">
                    {brand}
                  </span>
                </div>
                <span className="text-[10px] px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                  {productCount} article{productCount > 1 ? 's' : ''}
                </span>
              </button>
            );
          })}

          {/* Option de création si la marque n'existe pas */}
          {query.trim() && !isExactMatch && (
            <button
              type="button"
              onClick={handleCreateNew}
              onMouseEnter={() => setHighlightedIndex(filteredBrands.length)}
              className={`w-full text-left p-3 transition-all flex items-center gap-2 border-t-2 dark:border-blue-900/50 ${
                highlightedIndex === filteredBrands.length 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                  : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/10'
              }`}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white">
                <span className="text-sm font-bold">+</span>
              </div>
              <div>
                <span className="font-bold text-sm text-emerald-700 dark:text-emerald-400">
                  Créer "{query}"
                </span>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Nouvelle marque
                </p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Message si aucun résultat et aucune création possible */}
      {isOpen && filteredBrands.length === 0 && !query.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl p-4 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Tapez pour rechercher ou créer une marque
          </p>
        </div>
      )}
    </div>
  );
};

export default BrandAutocomplete;
